import { theme } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { PreferenceKey } from "types";
import { hexToRgba } from "./colorHelpers";
import { useInfiniteCollection } from "@/hooks/useInfiniteCollection";
import { findByKey } from "@/models/Preference";
import { DashboardColorPreference, DashboardCustomizationPreference } from "@/types/Dashboard";

interface SidebarColorProps {
    productId: ID;
    isCollapsed: boolean;
    capacity: { open: number; reserved: number };
    className?: string;
}

export const SidebarColor = React.memo(({ productId, isCollapsed, capacity, className = "" }: SidebarColorProps) => {
    const { data: preferences } = useInfiniteCollection<DashboardColorPreference>("preferences");

    const preference = findByKey(preferences, PreferenceKey.XOLA_SELLER_DASHBOARD_COLORS) as DashboardColorPreference;
    const sidebarPreference = preference ? preference.value.sidebar : DashboardCustomizationPreference.AVAILABILITY;
    const { open = 0, reserved = 0 } = capacity;

    return (
        <div data-product-id={productId} data-testid="sidebar-color" className={clsx("ui-sidebar-color", className)}>
            {sidebarPreference === DashboardCustomizationPreference.AVAILABILITY ? (
                <AvailabilityColor isCollapsed={isCollapsed} open={open} reserved={reserved} />
            ) : (
                <ListingColor isCollapsed={isCollapsed} productId={productId} preference={preference} />
            )}
        </div>
    );
});

interface AvailabilityColorProps {
    open: number;
    reserved: number;
    isCollapsed: boolean;
}

const AvailabilityColor = ({ open, reserved, isCollapsed }: AvailabilityColorProps) => {
    const { colors } = theme;
    const color = open <= 0 ? colors.danger.DEFAULT : open > reserved ? colors.success.DEFAULT : colors.warning.DEFAULT;
    return <SidebarBar isCollapsed={isCollapsed} backgroundColor={color} />;
};

interface ListingColorProps {
    productId: ID;
    isCollapsed: boolean;
    preference: DashboardColorPreference;
}

const ListingColor = React.memo(({ productId, isCollapsed, preference }: ListingColorProps) => {
    const backgroundColor = preference.value.experiences?.[productId ?? 0];
    if (!backgroundColor) {
        return null;
    }

    return <SidebarBar isCollapsed={isCollapsed} backgroundColor={backgroundColor} />;
});

const SidebarBar = ({ isCollapsed, backgroundColor }: { isCollapsed: boolean; backgroundColor: string }) => {
    const style = { backgroundColor: hexToRgba(backgroundColor) };
    return (
        <div
            data-testid="sidebar-color-style"
            className={clsx("h-full rounded", isCollapsed ? "w-2" : "w-4")}
            style={style}
        />
    );
};
